import { useEffect, useMemo, useState } from "react";
import { useGetList, useNotify } from "ra-core";
import { Link } from "react-router";

import { getSupabaseClient } from "../providers/supabase/supabase";

type Answer = { choice?: string; confidence?: number; probabilities?: Record<string, number> };
type Review = {
  id: number;
  contact_id: number;
  company_id: number | null;
  contact_name: string;
  linkedin_url: string | null;
  primary_title: string | null;
  company_name: string | null;
  crm_snapshot: Record<string, unknown>;
  jev_request: { state?: Record<string, unknown>; questions?: Record<string, unknown> };
  jev_response: { answers?: Record<string, Answer>; usage?: Record<string, number> };
  proposal: Record<string, unknown>;
  applied_fields: Record<string, unknown>;
  pending_fields: string[];
  hold_reasons: string[];
  created_at: string;
};

const FIELD_OPTIONS: Record<string, { label: string; options: string[]; jev: string }> = {
  person_role: { label: "Person role", options: ["buyer", "influencer", "other", "unknown"], jev: "person_role" },
  relationship_type: { label: "Relationship route", options: ["client", "tool partner", "delivery partner", "referral partner", "audience owner", "none", "unknown"], jev: "primary_relationship_type" },
  fit_quality: { label: "Potential strength", options: ["bullseye", "target", "stretch", "avoid", "unknown"], jev: "potential_strength" },
  company_model: { label: "Company model", options: ["operating", "consulting", "investment", "unknown"], jev: "company_model" },
  delivery_tool_relevance: { label: "Delivery-tool relevance", options: ["direct", "possible", "none", "unknown"], jev: "delivery_tool_relevance" },
};

// Which fields each classifier hold code affects, and what it means in plain words.
const HOLD_REASONS: Record<string, { fields: string[]; text: string }> = {
  low_confidence_person_role: { fields: ["person_role"], text: "Jev's confidence in this answer was below the 0.60 auto-write threshold" },
  conflict_with_existing_person_role: { fields: ["person_role"], text: "Jev's answer differs from the value already in CRM" },
  low_confidence_relationship_type: { fields: ["relationship_type"], text: "Jev's answer was unknown or below the 0.60 auto-write threshold" },
  client_requires_operating_company_model: { fields: ["relationship_type", "company_model"], text: "Jev said “client”, but the company isn't confirmed as an operating company" },
  conflict_with_existing_fit_quality: { fields: ["fit_quality"], text: "Jev's potential-strength answer differs from the value already in CRM" },
};

export function reasonsByField(pending: string[], holdReasons: string[]): Record<string, string[]> {
  const result: Record<string, string[]> = Object.fromEntries(pending.map(field => [field, [] as string[]]));
  for (const code of holdReasons ?? []) {
    const known = HOLD_REASONS[code];
    for (const field of known?.fields ?? pending) if (result[field]) result[field].push(known?.text ?? code.replaceAll("_", " "));
  }
  for (const field of pending) if (!result[field].length) result[field].push("Held by the classifier for review");
  return result;
}

export function selectedReviewChoices(values: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(values).filter(([field, value]) =>
    Boolean(value) && FIELD_OPTIONS[field]?.options.includes(value)));
}

const show = (value: unknown) => value == null || value === "" ? "—" :
  Array.isArray(value) ? (value.length ? value.join(", ") : "explicit no role") : String(value);

function ReviewDetail({ review, onSaved }: { review: Review; onSaved: () => void }) {
  const notify = useNotify();
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [fitConfidence, setFitConfidence] = useState("medium");
  const [personNote, setPersonNote] = useState("");
  const [feedback, setFeedback] = useState("");
  const [busy, setBusy] = useState(false);
  const [savedNotes, setSavedNotes] = useState<{ id: number; feedback_text: string; created_at: string }[]>([]);
  const reasons = reasonsByField(review.pending_fields ?? [], review.hold_reasons ?? []);
  const answers = review.jev_response?.answers ?? {};
  const state = review.jev_request?.state ?? {};

  useEffect(() => {
    setChoices({});
    setPersonNote("");
    setFeedback("");
    setFitConfidence("medium");
  }, [review.id]);

  const loadNotes = async () => {
    const { data } = await getSupabaseClient().from("contact_classification_feedback")
      .select("id, feedback_text, created_at").eq("review_id", review.id).order("created_at");
    setSavedNotes(data ?? []);
  };
  useEffect(() => { void loadNotes(); }, [review.id]);

  const saveFeedback = async () => {
    if (!feedback.trim()) return;
    setBusy(true);
    const { error } = await getSupabaseClient().from("contact_classification_feedback")
      .insert({ review_id: review.id, feedback_text: feedback.trim() });
    setBusy(false);
    if (error) return notify(error.message, { type: "error" });
    setFeedback("");
    void loadNotes();
    notify("Note saved for classifier review", { type: "success" });
  };

  const confirm = async () => {
    const decisions = selectedReviewChoices(choices);
    if (choices.fit_quality && decisions.fit_quality) decisions.fit_confidence = fitConfidence;
    // Partial decisions are allowed: unchosen fields are left exactly as they are.
    if (!Object.keys(decisions).length && !personNote.trim() && !feedback.trim()) {
      notify("Choose at least one field, or add a note", { type: "warning" });
      return;
    }
    setBusy(true);
    let error: { message: string } | null = null;
    try {
      const result = await Promise.race([
        getSupabaseClient().rpc("confirm_contact_classification_review", {
          p_review_id: review.id,
          p_choices: decisions,
          p_person_note: personNote.trim() || null,
          p_classifier_feedback: feedback.trim() || null,
        }),
        new Promise<{ error: { message: string } }>(resolve =>
          setTimeout(() => resolve({ error: { message: "Saving timed out; nothing was confirmed. Please retry." } }), 30000)),
      ]);
      error = result.error;
    } catch (caught) {
      error = { message: caught instanceof Error ? caught.message : String(caught) };
    } finally {
      setBusy(false);
    }
    if (error) return notify(error.message, { type: "error" });
    notify("Classification review confirmed", { type: "success" });
    onSaved();
  };

  return (
    <article className="space-y-5 rounded-xl border bg-card p-4">
      <div>
        <h2 className="text-xl font-semibold">{review.contact_name}</h2>
        <p className="text-sm text-muted-foreground">{show(review.primary_title)} at {show(review.company_name)}</p>
        <div className="mt-2 flex gap-4 text-sm underline">
          <Link to={`/contacts/${review.contact_id}/show`}>CRM record</Link>
          {review.linkedin_url && <a href={review.linkedin_url} target="_blank" rel="noreferrer">LinkedIn profile</a>}
        </div>
      </div>

      <section>
        <h3 className="font-semibold">Already written to CRM</h3>
        <div className="mt-1 text-sm">{Object.keys(review.applied_fields || {}).length ?
          Object.entries(review.applied_fields).map(([key, value]) => <p key={key}>{key}: {show(value)}</p>) :
          <p>None from this run.</p>}</div>
      </section>

      <section>
        <h3 className="font-semibold">Jev answers and confidence</h3>
        <p className="text-xs text-muted-foreground">Confidence is Jev's answer score, not measured accuracy.</p>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          {Object.entries(answers).map(([key, answer]) => (
            <div key={key} className="rounded border p-2 text-sm">
              <span className="font-medium">{key.replaceAll("_", " ")}</span>: {show(answer.choice)}
              {typeof answer.confidence === "number" && <span className="text-muted-foreground"> · {answer.confidence.toFixed(2)}</span>}
            </div>
          ))}
        </div>
        
      </section>

      <section>
        <h3 className="font-semibold">Your decisions</h3>
        <p className="text-sm text-muted-foreground">Fields flagged “Needs your decision” are why this contact is in review. You can override any field; fields left on “Leave unchanged” are not touched. “Unknown” records your judgment without inventing a route.</p>
        <div className="mt-2 grid gap-3 sm:grid-cols-2">
          {Object.keys(FIELD_OPTIONS)
            .filter(field => !(["company_model", "delivery_tool_relevance"].includes(field) && review.company_id == null))
            .sort((x, y) => Number(!!reasons[y]) - Number(!!reasons[x]))
            .map(field => {
              const definition = FIELD_OPTIONS[field];
              const current = review.crm_snapshot?.[field === "relationship_type" ? "relationship_types" : field];
              const raw = answers[definition.jev];
              const why = reasons[field];
              return <label key={field} className={`block rounded border p-2 text-sm ${why ? "border-amber-500 bg-amber-50 dark:bg-amber-950/30" : ""}`}>
                <span className="font-medium">{definition.label}</span>
                {why ? <span className="ml-2 rounded bg-amber-500 px-1.5 py-0.5 text-xs font-medium text-white">Needs your decision</span>
                  : <span className="ml-2 text-xs text-muted-foreground">confident — optional override</span>}
                {why && <span className="block text-amber-800 dark:text-amber-200">Why: {why.join("; ")}</span>}
                <span className="block text-muted-foreground">CRM: {show(current)} · Jev: {show(raw?.choice)} {typeof raw?.confidence === "number" ? `(${raw.confidence.toFixed(2)})` : ""}</span>
                <select className="mt-1 w-full rounded border bg-background p-2" value={choices[field] ?? ""}
                  onChange={event => setChoices(previous => ({ ...previous, [field]: event.target.value }))}>
                  <option value="">Leave unchanged</option>
                  {definition.options.map(value => <option key={value} value={value}>{value === "none" && field === "relationship_type" ? "No apparent BD motion" : value}</option>)}
                </select>
              </label>;
            })}
          {choices.fit_quality && <label className="block text-sm">
            <span className="font-medium">Evidence confidence</span>
            <select className="mt-1 w-full rounded border bg-background p-2" value={fitConfidence}
              onChange={event => setFitConfidence(event.target.value)}>
              {["high", "medium", "low", "unknown"].map(value => <option key={value}>{value}</option>)}
            </select>
          </label>}
        </div>
      </section>

      <section className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm"><span className="font-medium">Person note</span>
          <textarea className="mt-1 min-h-28 w-full rounded border bg-background p-2" value={personNote}
            onChange={event => setPersonNote(event.target.value)} placeholder="Context about this person; saved as a CRM note" /></label>
        <label className="block text-sm"><span className="font-medium">Note for improving the classifier</span>
          <textarea className="mt-1 min-h-28 w-full rounded border bg-background p-2" value={feedback}
            onChange={event => setFeedback(event.target.value)} placeholder="Why Jev got this wrong or was unsure, or a rule to add. Kept with this review (Jev's answers + your decisions) for later analysis" />
          <button type="button" disabled={busy || !feedback.trim()} onClick={saveFeedback}
            className="mt-1 rounded border px-3 py-1 disabled:opacity-50">Save note without confirming</button>
          {savedNotes.length > 0 && <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
            {savedNotes.map(note => <li key={note.id} className="rounded bg-muted p-2">{new Date(note.created_at).toLocaleString()}: {note.feedback_text}</li>)}
          </ul>}
        </label>
      </section>

      <details className="text-sm"><summary className="cursor-pointer font-medium">Evidence, exact Jev questions and response</summary>
        <p className="mt-2">Location: {show(state.profile_location)} · Company size: {show(state.company_size_band)}</p>
        <p>Headline: {show(state.profile_headline)}</p>
        <p>Company description: {show(state.company_description)}</p>
        <pre className="mt-2 max-h-96 overflow-auto whitespace-pre-wrap rounded bg-muted p-3 text-xs">{JSON.stringify({ request: review.jev_request, response: review.jev_response, proposal: review.proposal }, null, 2)}</pre>
      </details>

      <button type="button" disabled={busy} onClick={confirm}
        className="rounded bg-primary px-4 py-2 font-medium text-primary-foreground disabled:opacity-50">
        {busy ? "Saving…" : "Confirm selected classifications"}
      </button>
    </article>
  );
}

export const ClassificationReviewPage = () => {
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const { data = [], total = 0, isPending, refetch } = useGetList<Review>(
    "contact_classification_reviews",
    { pagination: { page, perPage: 25 }, sort: { field: "created_at", order: "DESC" }, filter: { status: "pending" } },
  );
  const selected = useMemo(() => data.find(item => item.id === selectedId) ?? data[0], [data, selectedId]);
  if (import.meta.env.VITE_IS_DEMO === "true") return <p className="p-4">Classification review requires the live CRM.</p>;
  return <div className="space-y-4 p-2 sm:p-4">
    <div><h1 className="text-2xl font-semibold">Pending classifications</h1>
      <p className="text-sm text-muted-foreground">{total} pending contacts. Confident fields are already in CRM; decide only the remaining fields.</p></div>
    {isPending ? <p>Loading reviews…</p> : data.length === 0 ? <p>No pending classifications.</p> :
      <div className="grid gap-4 lg:grid-cols-[17rem_1fr]">
        <aside className="space-y-1" aria-label="Pending contacts">
          {data.map(review => <button key={review.id} type="button" onClick={() => setSelectedId(review.id)}
            className={`block w-full rounded border p-2 text-left text-sm ${selected?.id === review.id ? "bg-accent" : "bg-card"}`}>
            <span className="font-medium">{review.contact_name}</span><span className="block truncate text-muted-foreground">{review.company_name}</span>
            <span className="block text-xs">{review.pending_fields.length} fields pending</span>
          </button>)}
          <div className="flex gap-2 pt-2">
            <button disabled={page <= 1} onClick={() => { setPage(page - 1); setSelectedId(null); }} className="rounded border px-2 py-1 disabled:opacity-50">Previous</button>
            <button disabled={page * 25 >= total} onClick={() => { setPage(page + 1); setSelectedId(null); }} className="rounded border px-2 py-1 disabled:opacity-50">Next</button>
          </div>
        </aside>
        {selected && <ReviewDetail key={selected.id} review={selected} onSaved={() => { setSelectedId(null); void refetch(); }} />}
      </div>}
  </div>;
};

ClassificationReviewPage.path = "/classification-reviews";
